import type { DocChatCapabilityManifest } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatCapabilities'

export const DOC_CHAT_MODEL_THINKING_LEVELS = {
  'gemini-3.6-flash': ['MINIMAL', 'LOW', 'MEDIUM', 'HIGH'],
  'gemini-3.7-flash': ['LOW', 'MEDIUM', 'HIGH'],
  'gemini-3.8-flash': ['LOW', 'MEDIUM', 'HIGH'],
} as const

export const DOC_CHAT_DEFAULT_MODEL =
  'gemini-3.6-flash' satisfies keyof typeof DOC_CHAT_MODEL_THINKING_LEVELS
export const DOC_CHAT_DEFAULT_THINKING_LEVEL =
  'MINIMAL' satisfies (typeof DOC_CHAT_MODEL_THINKING_LEVELS)[typeof DOC_CHAT_DEFAULT_MODEL][number]

export const DOC_CHAT_MODELS = Object.keys(DOC_CHAT_MODEL_THINKING_LEVELS)

/** A selectable model and its ordered thinking levels, from any source. */
export type DocChatModelOption = {
  model: string
  thinkingLevels: string[]
  defaultThinkingLevel: string
}

/** The switcher's resolved options: the ordered models plus the default. */
export type DocChatModelOptions = {
  models: DocChatModelOption[]
  defaultModel: string
}

/** The two capability fields the switcher reads; keyed off the manifest so the
 * shapes can't drift apart. */
type DocChatModelManifest = Pick<
  DocChatCapabilityManifest,
  'models' | 'default_model'
>

const BUILTIN_MODEL_OPTIONS: DocChatModelOptions = {
  models: DOC_CHAT_MODELS.map((model) => {
    const levels = thinkingLevelsForModel(model)
    return {
      model,
      thinkingLevels: levels,
      defaultThinkingLevel:
        model === DOC_CHAT_DEFAULT_MODEL
          ? DOC_CHAT_DEFAULT_THINKING_LEVEL
          : levels[0],
    }
  }),
  defaultModel: DOC_CHAT_DEFAULT_MODEL,
}

/**
 * Resolve the switcher's model options, preferring the backend capabilities
 * manifest and falling back to the built-in list when it omits models. Entries
 * without any thinking levels are dropped, and a default level that isn't one
 * of the model's levels is pulled back to the first — so every returned option
 * has a usable `defaultThinkingLevel`.
 */
export function resolveDocChatModelOptions(
  manifest: DocChatModelManifest | undefined
): DocChatModelOptions {
  const entries = (manifest?.models ?? []).filter(
    (entry) => entry.thinking_levels.length > 0
  )
  if (entries.length === 0) return BUILTIN_MODEL_OPTIONS
  const models = entries.map((entry) => ({
    model: entry.model,
    thinkingLevels: entry.thinking_levels,
    defaultThinkingLevel: entry.thinking_levels.includes(
      entry.default_thinking_level
    )
      ? entry.default_thinking_level
      : entry.thinking_levels[0],
  }))
  const defaultModel =
    manifest?.default_model &&
    models.some((m) => m.model === manifest.default_model)
      ? manifest.default_model
      : models[0].model
  return { models, defaultModel }
}

export function thinkingLevelsForModel(model: string): string[] {
  if (model in DOC_CHAT_MODEL_THINKING_LEVELS) {
    return [
      ...DOC_CHAT_MODEL_THINKING_LEVELS[
        model as keyof typeof DOC_CHAT_MODEL_THINKING_LEVELS
      ],
    ]
  }
  return [...DOC_CHAT_MODEL_THINKING_LEVELS[DOC_CHAT_DEFAULT_MODEL]]
}

/** Keep a thinking level the model allows, otherwise fall back to its default. */
export function coerceThinkingLevel(
  option: DocChatModelOption,
  thinkingLevel: string
): string {
  return option.thinkingLevels.includes(thinkingLevel)
    ? thinkingLevel
    : option.defaultThinkingLevel
}

/**
 * Reconcile a persisted choice against the live options: keep the stored model
 * when the backend still offers it (coercing its thinking level), otherwise fall
 * back to the default model and that model's default level. This is what keeps a
 * stale, retired, or cross-environment model from being sent to the backend.
 */
export function reconcileDocChatModelSettings(
  options: DocChatModelOptions,
  stored: { model: string; thinkingLevel: string }
): { model: string; thinkingLevel: string } {
  const matched = options.models.find((m) => m.model === stored.model)
  if (matched) {
    return {
      model: matched.model,
      thinkingLevel: coerceThinkingLevel(matched, stored.thinkingLevel),
    }
  }
  const fallback =
    options.models.find((m) => m.model === options.defaultModel) ??
    options.models[0]
  return { model: fallback.model, thinkingLevel: fallback.defaultThinkingLevel }
}
